import React, { useEffect, useState } from 'react'
import moment from 'moment'
import { useSelector, useDispatch } from 'react-redux'
import {
  postcodeValidator,
  postcodeValidatorExistsForCountry,
} from 'postcode-validator'

import styles from './TopUpBalanceModal.module.scss'

import {
  SandWatch as SandWatchIcon,
  InfoCircle as InfoIcon,
} from '../../../icons'
import TabsList from 'components/elements/tabs/TabsList'
import InfoNotice from 'components/elements/blocks/InfoNotice'
import CustomModal from 'components/elements/modal/CustomModal'
import InputGroup from 'components/elements/forms/InputGroup'
import CustomInput from 'components/elements/inputs/CustomInput'
import CustomButton from 'components/elements/buttons/CustomButton'
import { ISelectOption } from 'components/elements/inputs/CustomSelect/CustomSelect'
import CustomSelect from 'components/elements/inputs/CustomSelect'
import Flag from 'components/elements/other/Flag'

import { selectTopUpBalanceProcessing } from 'store/pricing/selectors'
import { selectUserBalanceView } from 'store/user/selectors'
import { ITopUpBalanceData } from 'models/pricing'
import { PricingActionsTypes } from 'store/pricing/actions'
import { minimalPaygTopUp, maximumPaygTopUp } from 'utils/constants'
import validUsZips from 'mocks/valid-us-zips.json'
import countriesList from 'mocks/countries-list.json'
import { selectUserCustomerView } from 'store/user/selectors'

interface ITopUpBalanceModal {
  isModalShown: boolean
  activeTopUpTabIndex: number
  onActiveTopUpTabIndex: (index: number) => void
  onModalClose: () => void
}

const tabItems = [
  {
    label: 'One-time purchase',
  },
  {
    label: 'Auto top-up',
  },
]

const TopUpBalanceModal: React.FC<ITopUpBalanceModal> = (props) => {
  const {
    isModalShown,
    activeTopUpTabIndex,
    onActiveTopUpTabIndex,
    onModalClose,
  } = props

  const dispatch = useDispatch()

  const topUpBalanceProcessing = useSelector(selectTopUpBalanceProcessing)
  const balanceInfo = useSelector(selectUserBalanceView)
  const customerInfo = useSelector(selectUserCustomerView)

  const [amount, setAmount] = useState('')
  const [autoAmount, setAutoAmount] = useState('')
  const [threshold, setThreshold] = useState('')
  const [wasStatusChanged, setStatusChange] = useState(false)
  const [isDeactivationNoticeShown, toggleDeactivationNoticeStatus] =
    useState(false)
  const [selectedCountry, setCountry] = useState<ISelectOption | null>(null)
  const [zipCode, setZipCode] = useState('')

  const availabilityDate = moment().add(1, 'year').format('DD MMMM YYYY')

  const handleTabChange = (index: number) => onActiveTopUpTabIndex(index)

  const handleCloseModal = () => {
    setAmount('')
    setAutoAmount('')
    setThreshold('')
    toggleDeactivationNoticeStatus(false)
    setStatusChange(false)

    onModalClose()
  }

  const handleCountryChange = (option: any) => {
    setCountry(option)
  }

  const isZipCodeValid = () => {
    if (!!selectedCountry && !!zipCode) {
      if (selectedCountry.value.toUpperCase() === 'US') {
        return validUsZips.indexOf(zipCode) >= 0
      } else if (
        postcodeValidatorExistsForCountry(selectedCountry.value.toUpperCase())
      ) {
        return postcodeValidator(zipCode, selectedCountry.value.toUpperCase())
      } else {
        return true
      }
    } else {
      return true
    }
  }

  const handleSubmit = () => {
    let topUpData: ITopUpBalanceData

    if (activeTopUpTabIndex === 0) {
      topUpData = {
        amount: +amount,
        one_time_operation: true,
        zip_code: zipCode || undefined,
        country: selectedCountry?.value,
      }
    } else {
      topUpData = {
        amount: +autoAmount,
        one_time_operation: false,
        threshold: threshold.toString(),
        zip_code: zipCode || undefined,
        country: selectedCountry?.value,
      }
    }

    dispatch({
      type: PricingActionsTypes.PRICING_TOP_UP_BALANCE,
      topUpData,
    })
  }

  const handleCancelAutoTopUp = () => {
    dispatch({ type: PricingActionsTypes.PRICING_CANCEL_AUTO_TOP_UP })
  }

  useEffect(() => {
    if (!!balanceInfo?.top_up_params) {
      setThreshold(balanceInfo.top_up_params.threshold)
      setAutoAmount(balanceInfo.top_up_params.amount)
      setStatusChange(true)
    }
  }, [balanceInfo?.top_up_params])

  useEffect(() => {
    if (wasStatusChanged && !balanceInfo?.top_up_params) {
      toggleDeactivationNoticeStatus(true)
    }
  }, [wasStatusChanged, balanceInfo?.top_up_params])

  const isBtnDisabled = () => {
    if (
      shouldBillingInfoFormShown &&
      (!zipCode || !selectedCountry || !isZipCodeValid())
    ) {
      return true
    } else if (activeTopUpTabIndex === 0) {
      if (!!amount && +amount < minimalPaygTopUp) {
        return true
      } else if (!!amount && +amount > maximumPaygTopUp) {
        return true
      } else if (!amount) {
        return true
      }
    } else if (activeTopUpTabIndex === 1) {
      if (!!autoAmount && +autoAmount < minimalPaygTopUp) {
        return true
      } else if (!!autoAmount && +autoAmount > maximumPaygTopUp) {
        return true
      } else if (!autoAmount || !threshold) {
        return true
      }
    } else {
      return false
    }
  }

  const amountInnerNotice = +amount > 0 ? `for $${amount} (+VAT)` : ''
  const autoAmountInnerNotice =
    +autoAmount > 0 ? `for $${autoAmount} (+VAT)` : ''

  const getCountriesOptions = () => {
    let options: ISelectOption[] = []

    if (countriesList) {
      Object.values(countriesList).forEach((location: any) => {
        options.push({
          label: location.name,
          value: location.code,
        })
      })
    }

    options.sort(function (a, b) {
      if (a.label < b.label) {
        return -1
      }
      if (a.label > b.label) {
        return 1
      }
      return 0
    })

    return options
  }

  const countriesOptions = getCountriesOptions()

  const iconedCountriesOptions = countriesOptions.map((item) => ({
    ...item,
    icon:
      item.value === 'any' ? null : (
        <Flag countryCode={item.value} className={styles.optionFlag} />
      ),
  }))

  const zipCodeLabel = !isZipCodeValid() ? 'ZIP - incorrect' : 'ZIP'

  const shouldBillingInfoFormShown =
    !customerInfo || !customerInfo.zip_code || !customerInfo.country

  const renderAutoBtn = () => {
    if (!!balanceInfo?.top_up_params) {
      return (
        <CustomButton
          className={styles.inputGroup}
          wide
          color={'outline'}
          loading={topUpBalanceProcessing}
          // disabled={isBtnDisabled()}
          onClick={handleCancelAutoTopUp}
        >
          Deactivate auto top-up
        </CustomButton>
      )
    } else {
      return (
        <CustomButton
          className={styles.inputGroup}
          wide
          loading={topUpBalanceProcessing}
          disabled={isBtnDisabled()}
          onClick={handleSubmit}
        >
          Activate auto top-up
        </CustomButton>
      )
    }
  }

  const renderActiveModalTab = () => {
    if (activeTopUpTabIndex === 0) {
      return (
        <div>
          <InputGroup
            className={styles.inputGroup}
            labelTop={`Top up amount (${minimalPaygTopUp} USD minimum)`}
            error={+amount > maximumPaygTopUp ? 'Exceeds max limit' : ''}
            isErrored={+amount > maximumPaygTopUp}
          >
            <CustomInput
              innerNotice={amountInnerNotice}
              value={amount}
              type={'number'}
              onChange={(e) => setAmount(e.target.value)}
            />
          </InputGroup>

          {shouldBillingInfoFormShown && (
            <div className={styles.row}>
              <InputGroup
                labelTop={'Country'}
                semiBoldLabel
                className={styles.group}
              >
                <CustomSelect
                  onChange={handleCountryChange}
                  value={selectedCountry}
                  name={'billing-info-country'}
                  options={iconedCountriesOptions}
                  placeholder={'Select country'}
                  isSearchable={true}
                  disabled={topUpBalanceProcessing}
                />
              </InputGroup>

              <InputGroup
                labelTop={zipCodeLabel}
                semiBoldLabel
                className={styles.zipGroup}
                isErrored={!isZipCodeValid()}
              >
                <CustomInput
                  value={zipCode}
                  onChange={(e) => setZipCode(e.target.value)}
                  readOnly={topUpBalanceProcessing}
                  error={!isZipCodeValid()}
                />
              </InputGroup>
            </div>
          )}

          <CustomButton
            wide
            loading={topUpBalanceProcessing}
            disabled={isBtnDisabled()}
            onClick={handleSubmit}
          >
            Top up
          </CustomButton>
        </div>
      )
    } else {
      return (
        <div>
          <InputGroup
            className={styles.inputGroup}
            labelTop={'Minimum balance threshold'}
          >
            <CustomInput
              innerNotice={'USD'}
              value={threshold}
              type={'number'}
              readOnly={!!balanceInfo?.top_up_params}
              onChange={(e) => setThreshold(e.target.value)}
            />
          </InputGroup>

          <InputGroup
            className={styles.inputGroup}
            labelTop={`Amount to add (${minimalPaygTopUp} USD minimum)`}
            childrenDirection={'vertical'}
            bottomNotice={
              !!balanceInfo?.top_up_params
                ? 'If you want to edit, please deactivate and set Auto top up again'
                : ''
            }
            error={+autoAmount > maximumPaygTopUp ? 'Exceeds max limit' : ''}
            isErrored={+autoAmount > maximumPaygTopUp}
          >
            <CustomInput
              innerNotice={autoAmountInnerNotice}
              value={autoAmount}
              type={'number'}
              readOnly={!!balanceInfo?.top_up_params}
              onChange={(e) => setAutoAmount(e.target.value)}
            />
          </InputGroup>

          {!!balanceInfo?.top_up_params && (
            <InfoNotice
              color={'green'}
              icon={<SandWatchIcon />}
              className={styles.autoTopUpNotice}
            >
              Auto top-up activated
            </InfoNotice>
          )}

          {isDeactivationNoticeShown && (
            <InfoNotice
              color={'blue'}
              icon={<InfoIcon />}
              className={styles.autoTopUpNotice}
            >
              Auto top-up deactivated
            </InfoNotice>
          )}

          {shouldBillingInfoFormShown && (
            <div className={styles.row}>
              <InputGroup
                labelTop={'Country'}
                semiBoldLabel
                className={styles.group}
              >
                <CustomSelect
                  onChange={handleCountryChange}
                  value={selectedCountry}
                  name={'billing-info-country'}
                  options={iconedCountriesOptions}
                  placeholder={'Select country'}
                  isSearchable={true}
                  disabled={topUpBalanceProcessing}
                />
              </InputGroup>

              <InputGroup
                labelTop={zipCodeLabel}
                semiBoldLabel
                className={styles.zipGroup}
                isErrored={!isZipCodeValid()}
              >
                <CustomInput
                  value={zipCode}
                  onChange={(e) => setZipCode(e.target.value)}
                  readOnly={topUpBalanceProcessing}
                  error={!isZipCodeValid()}
                />
              </InputGroup>
            </div>
          )}

          {renderAutoBtn()}

          <div className={styles.modalBottom}>
            <a
              href="https://helpcenter.soax.com/en/articles/9925418-how-auto-top-up-works"
              target={'_blank'}
              rel={'noopener noreferrer'}
            >
              Learn more about auto top-up
            </a>
          </div>
        </div>
      )
    }
  }

  return (
    <CustomModal
      title={'Top up balance'}
      isModalOpen={isModalShown}
      shouldCloseOnOverlayClick
      showCross
      onModalClose={handleCloseModal}
    >
      <>
        <TabsList
          items={tabItems}
          activeTabIndex={activeTopUpTabIndex}
          size={'l'}
          type={'bordered'}
          color={'blue'}
          className={styles.modalTabs}
          wide
          onChange={handleTabChange}
        />

        <InfoNotice
          color={'gray'}
          icon={<SandWatchIcon />}
          className={styles.modalNotice}
        >
          Any amount you add now will be available for 12 months (
          {availabilityDate}).
        </InfoNotice>

        {renderActiveModalTab()}
      </>
    </CustomModal>
  )
}

export default TopUpBalanceModal
